import { parseApiInstant } from './apiTime';

/**
 * Shared Intl-based formatters for date and currency values.
 *
 * Both helpers follow the same locale-fallback contract:
 *   `locale || undefined` — passing an empty string to `Intl` constructors would
 *   throw in some runtimes; coercing to `undefined` causes the browser to use its
 *   own default locale instead, which is the desired fallback behavior.
 *
 * Em-dash (`—`) is the canonical null/undefined/error display value throughout
 * the iTow UI, mirroring the Angular template convention.
 */

/**
 * Format an ISO date string using `Intl.DateTimeFormat`.
 *
 * Parsed through `parseApiInstant`, not `new Date` directly: the API sends UTC
 * without a `Z`, which the language reads as local time. Every timestamp in
 * this portal was rendering that many hours early until 23 September — five in
 * Central, six in Mountain — and the fault was invisible because a wrong time
 * still looks like a time.
 *
 * @param value  ISO date/datetime string, or null/undefined.
 * @param locale BCP 47 locale tag (e.g. `"en-US"`, `"es-MX"`). Empty string
 *               falls through to the browser default locale.
 * @returns Formatted date string, or `"—"` on null/undefined/parse failure.
 */
export function formatDate(
  value: string | null | undefined,
  locale: string,
  timezone?: string,
): string {
  if (value == null) return '—'; // em-dash
  try {
    const at = parseApiInstant(value);
    if (at === null) return '—'; // em-dash
    const opts: Intl.DateTimeFormatOptions | undefined = timezone ? { timeZone: timezone } : undefined;
    return new Intl.DateTimeFormat(locale || undefined, opts).format(at);
  } catch {
    return '—';
  }
}

/**
 * Format a numeric value as USD currency using `Intl.NumberFormat`.
 *
 * @param value  Numeric amount, or null/undefined.
 * @param locale BCP 47 locale tag (e.g. `"en-US"`, `"es-MX"`). Empty string
 *               falls through to the browser default locale.
 * @returns Formatted currency string, or `"—"` on null/undefined.
 */
export function formatCurrency(
  value: number | null | undefined,
  locale: string,
): string {
  if (value == null) return '—'; // em-dash
  return new Intl.NumberFormat(locale || undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}
